/**
 * Enum for surface types
 */
export enum SurfaceEnum {
  VELODROME = 0,
  PAVEMENT = 10,
  COBBLES = 20,
  GRAVEL = 30,
  DIRT = 40,
}

/**
 * Maps surface enum values to their names
 */
const SURFACE_NAMES: Record<number, string> = {
  0: 'Velodrome',
  10: 'Pavement',
  20: 'Cobbles',
  30: 'Gravel',
  40: 'Dirt',
};

/**
 * Interface for the SurfaceQuality object
 */
export interface ISurfaceQuality {
  surface_type: SurfaceEnum;
  quality: number;
}

/**
 * Pavement BRI (Bicycle Roughness Index) descriptions
 */
const PAVEMENT_BRI_DESCRIPTION: Record<number, [string, string]> = {
  0: [
    'Very Smooth',
    'Recently paved, smooth blacktop, or high-quality concrete bike paths. Minimal to no perceptible vibration through the handlebars.',
  ],
  0.2: ['Smooth', 'Standard asphalt in good condition. You can feel a slight texture, but not uncomfortable.'],
  0.3: [
    'Fair',
    'Older asphalt with some cracking and surface wear. A noticeable and constant "buzz" or vibration is felt.',
  ],
  0.4: [
    'Rough',
    'Coarse chip seal, or pavement with frequent cracks and minor potholes. The ride can be uncomfortable over longer distances. ',
  ],
  0.5: [
    'Very Rough',
    'Significantly deteriorated pavement with potholes and large cracks that require the rider to pay close attention to their line choice. Uncomfortable for most riding.',
  ],
};

/**
 * SurfaceQuality class that replicates Python SurfaceQuality behavior
 */
export class SurfaceQuality implements ISurfaceQuality {
  surface_type: SurfaceEnum;
  quality: number;

  constructor(surfaceType: SurfaceEnum = SurfaceEnum.PAVEMENT, quality: number = 0.25) {
    this.surface_type = surfaceType;
    this.quality = quality;
  }

  /**
   * Creates a SurfaceQuality object from a float value
   * The integer part represents the surface type, the decimal part represents the quality
   * If value is null or undefined, defaults to 10.25 (Pavement with quality 0.25)
   */
  static fromFloat(value: number | null | undefined): SurfaceQuality {
    const actualValue = value ?? 10.25;
    const integer = Math.floor(actualValue);
    const quality = Math.round((actualValue - integer) * 100000000) / 100000000; // Round to 8 decimal places

    return new SurfaceQuality(integer as SurfaceEnum, quality);
  }

  /**
   * Validates that quality is between 0 and 1
   */
  static validQuality(quality: number | null | undefined): void {
    if (quality === null || quality === undefined) {
      return;
    }
    if (quality < 0 || quality > 1) {
      throw new Error(`Surface Quality must be between 0 and 1, got ${quality}`);
    }
  }

  /**
   * Gets the pavement description tuple based on quality value
   */
  private getPavedTuple(): [string, string] {
    const keys = Object.keys(PAVEMENT_BRI_DESCRIPTION).map(Number).reverse();

    for (const key of keys) {
      if (this.quality > key) {
        return PAVEMENT_BRI_DESCRIPTION[key];
      }
    }
    return PAVEMENT_BRI_DESCRIPTION[0];
  }

  /**
   * Gets the surface name from the enum value
   */
  private getSurfaceName(): string {
    return SURFACE_NAMES[this.surface_type] || 'Unknown';
  }

  /**
   * Gets a short description of the surface quality
   */
  getDescription(): string {
    const definer = ` Q: ${this.quality}`;
    if (this.surface_type === SurfaceEnum.PAVEMENT) {
      return `${this.getSurfaceName()}: ${this.getPavedTuple()[0]}, ${definer}`;
    } else {
      return `${this.getSurfaceName()}: ${definer}`;
    }
  }

  /**
   * Gets the full descriptive text for the surface quality
   */
  getFullText(): string {
    const generalMsg =
      'Quality is a value between 0 and 1. ' +
      'It is currently subjective and only used for displaying results. ' +
      'Higher resolution, such as 0.24 can be used to differentiate small differences.';

    let additionalMsg =
      `For ${this.getSurfaceName().toLowerCase()}, no standard description has been set yet, ` +
      'but 0.0 is very smooth and greater than 0.5 is very rough.';

    if (this.surface_type === SurfaceEnum.PAVEMENT) {
      const tuple = this.getPavedTuple();
      additionalMsg = `\n${tuple[0]}: ${tuple[1]}`;
    }

    return generalMsg + additionalMsg;
  }

  /**
   * Converts the object to a dictionary format matching the Python to_dict() method
   */
  toDict(): {
    quality: number;
    surface_type: number;
    surface_type_description: string;
    description: string;
    full_text: string;
    value: number;
  } {
    return {
      quality: this.quality,
      surface_type: this.surface_type,
      surface_type_description: this.getSurfaceName(),
      description: this.getDescription(),
      full_text: this.getFullText(),
      value: this.surface_type + this.quality,
    };
  }

  /**
   * Serializes to a float value (surface_type + quality)
   */
  toFloat(): number {
    return this.surface_type + this.quality;
  }
}
